import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";

import { medicalService, type Clinician, type ClinicLearner, type MedicalRecord, type MedicalVisit } from "../../services/medicalService";
import { useAuthStore } from "../../stores/authStore";
import { apiErrorMessage } from "../../utils/apiError";
import { daysFromTodayIso, nowLocalInput, todayIso } from "../../utils/dates";
import { Field, FormSection } from "../FormLayout";
import { FormModal } from "../FormModal";
import { ClinicalAlerts, LearnerPick } from "./ClinicParts";
import { PRIORITIES, VISIT_TYPES, clock, day, labelFrom } from "./medicalLogic";

const text = (data: FormData, name: string) => String(data.get(name) ?? "").trim();

/**
 * A learner at the sick bay, or a check-up booked for later. A learner who is
 * here now is booked and started in one go, so the visit opens straight away.
 */
export function NewVisitForm({ learners, records, clinicians, onClose, onDone }: {
  learners: ClinicLearner[];
  records: MedicalRecord[];
  /** Empty when the list could not be read; the server then books the visit to whoever records it. */
  clinicians: Clinician[];
  onClose: () => void;
  onDone: (visit: MedicalVisit, text: string) => void;
}) {
  const me = useAuthStore((state) => state.user);
  const today = todayIso();
  const [learner, setLearner] = useState("");
  const [here, setHere] = useState(true);
  const mine = clinicians.find((clinician) => clinician.user === me?.id);
  const record = records.find((row) => String(row.student) === learner);
  const save = useMutation({
    mutationFn: async ({ payload, startNow }: { payload: Record<string, unknown>; startNow: boolean }) => {
      const visit = await medicalService.scheduleVisit(payload);
      return startNow ? medicalService.startVisit(visit.id) : visit;
    },
    onSuccess: (visit) =>
      onDone(
        visit,
        visit.status === "in_progress"
          ? `${visit.student_name} is with the clinician now. Complete the visit when you are done.`
          : `${visit.student_name} is booked for ${day(visit.visit_date)} at ${clock(visit.visit_time)}.`,
      ),
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const [date, time] = here ? nowLocalInput().split("T") : [text(data, "date"), text(data, "time")];
    save.mutate({
      startNow: here,
      payload: {
        student_id: Number(learner),
        medical_staff_id: data.get("clinician") ? Number(data.get("clinician")) : undefined,
        visit_type: text(data, "visit_type"),
        priority: text(data, "priority"),
        visit_date: date,
        visit_time: time,
        chief_complaint: text(data, "complaint"),
        symptoms: text(data, "symptoms") || null,
        duration: text(data, "duration") || null,
      },
    });
  };

  return (
    <FormModal
      id="new-visit"
      title="New sick-bay visit"
      busy={save.isPending}
      error={save.isError ? apiErrorMessage(save.error, "The visit could not be recorded.") : undefined}
      saveLabel={here ? "Start the visit" : "Book the visit"}
      note={here ? "Recorded for today at the current time." : undefined}
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="The learner" divided={false}>
        <LearnerPick learners={learners} value={learner} onChange={setLearner} />
        {learner && (
          <div className="sm:col-span-2">
            <ClinicalAlerts record={record} missingText="This learner has no health record yet, so any allergies are unknown. Ask before giving medicine." />
          </div>
        )}
      </FormSection>

      <FormSection title="The visit">
        <Field label="When" as="div" className="sm:col-span-2">
          <div className="segmented">
            <button type="button" aria-pressed={here} onClick={() => setHere(true)} className="segmented-option">
              The learner is here now
            </button>
            <button type="button" aria-pressed={!here} onClick={() => setHere(false)} className="segmented-option">
              Book for later
            </button>
          </div>
        </Field>
        {!here && (
          <>
            <Field label="Date">
              <input required type="date" name="date" min={today} defaultValue={daysFromTodayIso(1)} className="input mt-1" />
            </Field>
            <Field label="Time">
              <input required type="time" name="time" defaultValue="09:00" className="input mt-1" />
            </Field>
          </>
        )}
        <Field label="Kind of visit">
          <select name="visit_type" defaultValue="sick_visit" className="input mt-1">
            {VISIT_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </Field>
        <Field label="Priority" hint="Urgent visits go to the top of the queue.">
          <select name="priority" defaultValue="medium" className="input mt-1">
            {PRIORITIES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </Field>
        {clinicians.length > 0 && (
          <Field label="Seen by" className="sm:col-span-2">
            <select name="clinician" defaultValue={mine?.id ?? clinicians[0].id} className="input mt-1">
              {clinicians.map((clinician) => (
                <option key={clinician.id} value={clinician.id}>
                  {clinician.user_name || `Clinician ${clinician.id}`} · {labelFrom([], clinician.staff_type)}
                </option>
              ))}
            </select>
          </Field>
        )}
      </FormSection>

      <FormSection title="What is wrong">
        <Field label="Main complaint" className="sm:col-span-2" hint="In the learner's words, e.g. “headache since break time”.">
          <textarea required name="complaint" rows={2} className="input mt-1" />
        </Field>
        <Field label="Symptoms" optional>
          <textarea name="symptoms" rows={2} className="input mt-1" />
        </Field>
        <Field label="How long" optional hint="e.g. since morning, two days.">
          <input name="duration" className="input mt-1" />
        </Field>
      </FormSection>
    </FormModal>
  );
}

/** What was found, what was done, and what happens next. */
export function CompleteVisitForm({ visit, onClose, onDone }: { visit: MedicalVisit; onClose: () => void; onDone: (visit: MedicalVisit) => void }) {
  const [followUp, setFollowUp] = useState(false);
  const [referral, setReferral] = useState(false);
  const save = useMutation({ mutationFn: medicalService.completeVisit, onSuccess: onDone });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const vitals = Object.fromEntries(
      ["temperature", "pulse_rate", "respiratory_rate", "blood_pressure", "oxygen_saturation", "weight"].map((key) => [key, text(data, key)]).filter(([, value]) => value),
    );
    save.mutate({
      id: visit.id,
      data: {
        vital_signs: vitals,
        physical_examination: text(data, "examination") || null,
        diagnosis: text(data, "diagnosis"),
        treatment_plan: text(data, "treatment"),
        procedures_performed: text(data, "procedures") || null,
        follow_up_required: followUp,
        follow_up_date: followUp ? text(data, "follow_up_date") : null,
        follow_up_notes: followUp ? text(data, "follow_up_notes") || null : null,
        referral_required: referral,
        referral_to: referral ? text(data, "referral_to") : null,
        referral_reason: referral ? text(data, "referral_reason") || null : null,
        notes: text(data, "notes") || null,
      },
    });
  };

  return (
    <FormModal
      id="complete-visit"
      title={`Complete the visit · ${visit.student_name}`}
      busy={save.isPending}
      error={save.isError ? apiErrorMessage(save.error, "The visit could not be completed.") : undefined}
      saveLabel="Complete the visit"
      note="Medicine is prescribed from the visit afterwards."
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="Vital signs" description="Leave out whatever was not taken." columns={3} divided={false}>
        <Field label="Temperature (°C)" optional>
          <input type="number" name="temperature" min={30} max={45} step="0.1" className="input mt-1 tabular-nums" />
        </Field>
        <Field label="Pulse (per minute)" optional>
          <input type="number" name="pulse_rate" min={20} max={250} className="input mt-1 tabular-nums" />
        </Field>
        <Field label="Breathing (per minute)" optional>
          <input type="number" name="respiratory_rate" min={5} max={80} className="input mt-1 tabular-nums" />
        </Field>
        <Field label="Blood pressure" optional hint="e.g. 110/70">
          <input name="blood_pressure" pattern="\d{2,3}/\d{2,3}" className="input mt-1 tabular-nums" />
        </Field>
        <Field label="Oxygen (SpO₂ %)" optional>
          <input type="number" name="oxygen_saturation" min={50} max={100} className="input mt-1 tabular-nums" />
        </Field>
        <Field label="Weight (kg)" optional>
          <input type="number" name="weight" min={1} max={200} step="0.1" className="input mt-1 tabular-nums" />
        </Field>
      </FormSection>

      <FormSection title="Assessment">
        <Field label="Examination" optional className="sm:col-span-2">
          <textarea name="examination" rows={2} className="input mt-1" />
        </Field>
        <Field label="Diagnosis or assessment" className="sm:col-span-2">
          <textarea required name="diagnosis" rows={2} className="input mt-1" />
        </Field>
        <Field label="Treatment given or planned" className="sm:col-span-2">
          <textarea required name="treatment" rows={2} className="input mt-1" />
        </Field>
        <Field label="Procedures done" optional className="sm:col-span-2" hint="e.g. wound cleaned and dressed.">
          <input name="procedures" className="input mt-1" />
        </Field>
      </FormSection>

      <FormSection title="What happens next">
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 sm:col-span-2">
          <input type="checkbox" checked={followUp} onChange={(event) => setFollowUp(event.target.checked)} className="h-4 w-4 rounded border-gray-300" />
          Come back for a follow-up
        </label>
        {followUp && (
          <>
            <Field label="Follow-up on">
              <input required type="date" name="follow_up_date" min={daysFromTodayIso(1)} defaultValue={daysFromTodayIso(3)} className="input mt-1" />
            </Field>
            <Field label="What to check" optional>
              <input name="follow_up_notes" className="input mt-1" />
            </Field>
          </>
        )}
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 sm:col-span-2">
          <input type="checkbox" checked={referral} onChange={(event) => setReferral(event.target.checked)} className="h-4 w-4 rounded border-gray-300" />
          Refer to a hospital or specialist
        </label>
        {referral && (
          <>
            <Field label="Referred to">
              <input required name="referral_to" className="input mt-1" placeholder="e.g. Mulago National Referral Hospital" />
            </Field>
            <Field label="Why" optional>
              <input name="referral_reason" className="input mt-1" />
            </Field>
          </>
        )}
        <Field label="Other notes" optional className="sm:col-span-2" hint="e.g. sent home, parent called, back to class.">
          <textarea name="notes" rows={2} className="input mt-1" />
        </Field>
      </FormSection>
    </FormModal>
  );
}
